import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL, API_URL_Image } from '../../api/api';
import { FiUser, FiMail, FiPhone, FiLock, FiCheck, FiX, FiUpload, FiMapPin } from 'react-icons/fi';

const API_USERS = `${API_URL}/user`;
const API_ROLES = `${API_URL}/roles`;
const token = localStorage.getItem('token');

const AddNewuserLayer = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = Boolean(id);

    const [roles, setRoles] = useState([]);
    const [imagePreview, setImagePreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [userData, setUserData] = useState({
        name: '',
        role: '',
        email: '',
        password: '',
        confirmPassword: '',
        mobile: '',
        username: '',
        address: '',
        status: 'active',
        image: null,
    });

    useEffect(() => {
        fetchRoles();
        if (isEditMode) {
            fetchUserById(id);
        }
    }, [id, isEditMode]);

    useEffect(() => {
        if (userData.password) {
            calculatePasswordStrength(userData.password);
        } else {
            setPasswordStrength(0);
        }
    }, [userData.password]);

    const fetchRoles = async () => {
        try {
            const response = await axios.get(API_ROLES, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setRoles(response.data || []);
        } catch (error) {
            console.error('Error fetching roles:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load roles. Please try again.',
            });
        }
    };

    const fetchUserById = async (userId) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_USERS}/${userId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const user = res.data;

            setUserData({
                name: user.name || '',
                role: user.role || '',
                email: user.email || '',
                password: '',
                confirmPassword: '',
                mobile: user.mobile || user.phone || '',
                username: user.username || '',
                address: user.address || '',
                status: user.status || 'active',
                image: null,
            });

            if (user.image) {
                setImagePreview(`${API_URL_Image}/${user.image}`);
            }
        } catch (error) {
            console.error('Error fetching user:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load user data.',
            });
        } finally {
            setLoading(false);
        }
    };

    const calculatePasswordStrength = (password) => {
        let strength = 0;
        if (password.length >= 8) strength += 25;
        if (/[A-Z]/.test(password)) strength += 25;
        if (/[0-9]/.test(password)) strength += 25;
        if (/[^A-Za-z0-9]/.test(password)) strength += 25;
        setPasswordStrength(strength);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!userData.name.trim()) newErrors.name = 'Name is required';

        if (!userData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(userData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (!userData.mobile.trim()) {
            newErrors.mobile = 'Mobile is required';
        } else if (!/^\d{10,15}$/.test(userData.mobile)) {
            newErrors.mobile = 'Mobile number is invalid (10-15 digits)';
        }

        if (!userData.username.trim()) newErrors.username = 'Username is required';
        if (!userData.role) newErrors.role = 'Role is required';
        if (!userData.status) newErrors.status = 'Status is required';

        if (!isEditMode) {
            if (!userData.password) {
                newErrors.password = 'Password is required';
            } else if (userData.password.length < 8) {
                newErrors.password = 'Password must be at least 8 characters';
            }

            if (userData.password !== userData.confirmPassword) {
                newErrors.confirmPassword = 'Passwords do not match';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setUserData(prev => ({ ...prev, [name]: value }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid File',
                    text: 'Please select an image file (JPG, PNG, GIF).',
                });
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                Swal.fire({
                    icon: 'error',
                    title: 'File Too Large',
                    text: 'Image size should be less than 5MB.',
                });
                return;
            }

            setImagePreview(URL.createObjectURL(file));
            setUserData(prev => ({ ...prev, image: file }));
        }
    };

    const handleSubmitUser = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            Swal.fire({
                icon: 'warning',
                title: 'Validation Error',
                text: 'Please fill all required fields correctly.',
            });
            return;
        }

        if (!isEditMode && userData.password !== userData.confirmPassword) {
            Swal.fire({
                icon: 'error',
                title: 'Password Mismatch',
                text: 'Passwords do not match!',
            });
            return;
        }

        const formData = new FormData();

        // Append all form data
        Object.keys(userData).forEach(key => {
            if (key === 'image' && userData[key]) {
                formData.append(key, userData[key]);
            } else if (key !== 'image' && userData[key] !== null && userData[key] !== undefined && userData[key] !== '') {
                formData.append(key, userData[key]);
            }
        });

        setLoading(true);
        try {
            if (isEditMode) {
                // For update, send PUT request
                await axios.put(`${API_USERS}/${id}`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                    },
                });
            } else {
                // For create, send POST request
                await axios.post(API_USERS, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                    },
                });
            }

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: `User ${isEditMode ? 'updated' : 'added'} successfully!`,
                timer: 2000,
                showConfirmButton: false,
                background: '#f0f9ff',
                color: '#0c4a6e',
            });

            // Redirect after 2 seconds
            setTimeout(() => {
                navigate('/user-list');
            }, 2000);

        } catch (error) {
            console.error('Error saving user:', error);

            let errorMessage = 'Failed to save user. Please try again.';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.errors) {
                // Handle Laravel validation errors
                const errorMessages = Object.values(error.response.data.errors).flat();
                errorMessage = errorMessages.join(', ');
            }

            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: errorMessage,
            });
        } finally {
            setLoading(false);
        }
    };

    const getPasswordStrengthColor = () => {
        if (passwordStrength <= 25) return '#ef4444'; // Red
        if (passwordStrength <= 50) return '#f97316'; // Orange
        if (passwordStrength <= 75) return '#eab308'; // Yellow
        return '#22c55e'; // Green
    };

    const handleCancel = () => {
        navigate('/users-list');
    };

    const handleImageRemove = () => {
        setImagePreview(null);
        setUserData(prev => ({ ...prev, image: null }));
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';
    };

    const getPasswordStrengthText = () => {
        if (passwordStrength <= 25) return 'Weak';
        if (passwordStrength <= 50) return 'Fair';
        if (passwordStrength <= 75) return 'Good';
        return 'Strong';
    };

    if (loading && isEditMode) {
        return (
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">Loading user data...</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-gradient-primary text-white py-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h5 className="mb-0">
                                        <FiUser className="me-2" />
                                        {isEditMode ? 'Update User' : 'Add New User'}
                                    </h5>
                                    <p className="mb-0 opacity-75 small">
                                        {isEditMode ? 'Edit user information' : 'Create a new user account'}
                                    </p>
                                </div>
                                <button
                                    onClick={handleCancel}
                                    className="btn btn-light btn-sm"
                                >
                                    <FiX className="me-1" /> Cancel
                                </button>
                            </div>
                        </div>

                        <div className="card-body p-4">
                            <form onSubmit={handleSubmitUser} encType="multipart/form-data">
                                <div className="row g-4">
                                    {/* Left Column - Basic Information */}
                                    <div className="col-lg-8">
                                        <div className="card h-100 border-0 shadow-sm">
                                            <div className="card-header bg-light py-3">
                                                <h6 className="mb-0">
                                                    <FiUser className="me-2" />
                                                    Basic Information
                                                </h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-semibold">
                                                            <FiUser className="me-1" />
                                                            Full Name *
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="name"
                                                            value={userData.name}
                                                            onChange={handleInputChange}
                                                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                                            placeholder="Enter full name"
                                                        />
                                                        {errors.name && (
                                                            <div className="invalid-feedback">{errors.name}</div>
                                                        )}
                                                    </div>

                                                    <div className="col-md-6">
                                                        <label className="form-label fw-semibold">
                                                            <FiMail className="me-1" />
                                                            Email *
                                                        </label>
                                                        <input
                                                            type="email"
                                                            name="email"
                                                            value={userData.email}
                                                            onChange={handleInputChange}
                                                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                                            placeholder="user@example.com"
                                                        />
                                                        {errors.email && (
                                                            <div className="invalid-feedback">{errors.email}</div>
                                                        )}
                                                    </div>

                                                    <div className="col-md-6">
                                                        <label className="form-label fw-semibold">
                                                            <FiPhone className="me-1" />
                                                            Mobile Number *
                                                        </label>
                                                        <input
                                                            type="tel"
                                                            name="mobile"
                                                            value={userData.mobile}
                                                            onChange={handleInputChange}
                                                            className={`form-control ${errors.mobile ? 'is-invalid' : ''}`}
                                                            placeholder="10-15 digits"
                                                        />
                                                        {errors.mobile && (
                                                            <div className="invalid-feedback">{errors.mobile}</div>
                                                        )}
                                                    </div>

                                                    <div className="col-md-6">
                                                        <label className="form-label fw-semibold">
                                                            <FiUser className="me-1" />
                                                            Username *
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="username"
                                                            value={userData.username}
                                                            onChange={handleInputChange}
                                                            className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                                                            placeholder="Enter username"
                                                        />
                                                        {errors.username && (
                                                            <div className="invalid-feedback">{errors.username}</div>
                                                        )}
                                                    </div>

                                                    {!isEditMode && (
                                                        <>
                                                            <div className="col-md-6">
                                                                <label className="form-label fw-semibold">
                                                                    <FiLock className="me-1" />
                                                                    Password *
                                                                </label>
                                                                <input
                                                                    type="password"
                                                                    name="password"
                                                                    value={userData.password}
                                                                    onChange={handleInputChange}
                                                                    className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                                                    placeholder="Minimum 8 characters"
                                                                />
                                                                {errors.password && (
                                                                    <div className="invalid-feedback">{errors.password}</div>
                                                                )}
                                                                {userData.password && (
                                                                    <div className="mt-2">
                                                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                                                            <small className="text-muted">
                                                                                Password strength: <span style={{ color: getPasswordStrengthColor() }}>
                                                                                    {getPasswordStrengthText()}
                                                                                </span>
                                                                            </small>
                                                                            <small>{passwordStrength}%</small>
                                                                        </div>
                                                                        <div className="progress" style={{ height: '6px' }}>
                                                                            <div
                                                                                className="progress-bar"
                                                                                role="progressbar"
                                                                                style={{
                                                                                    width: `${passwordStrength}%`,
                                                                                    backgroundColor: getPasswordStrengthColor(),
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className="col-md-6">
                                                                <label className="form-label fw-semibold">
                                                                    <FiLock className="me-1" />
                                                                    Confirm Password *
                                                                </label>
                                                                <input
                                                                    type="password"
                                                                    name="confirmPassword"
                                                                    value={userData.confirmPassword}
                                                                    onChange={handleInputChange}
                                                                    className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                                                                    placeholder="Confirm your password"
                                                                />
                                                                {errors.confirmPassword && (
                                                                    <div className="invalid-feedback">{errors.confirmPassword}</div>
                                                                )}
                                                                {userData.password && userData.confirmPassword && (
                                                                    <small className={`d-block mt-1 ${userData.password === userData.confirmPassword ? 'text-success' : 'text-danger'}`}>
                                                                        {userData.password === userData.confirmPassword ? (
                                                                            <FiCheck className="me-1" />
                                                                        ) : (
                                                                            <FiX className="me-1" />
                                                                        )}
                                                                        {userData.password === userData.confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                                                                    </small>
                                                                )}
                                                            </div>
                                                        </>
                                                    )}

                                                    <div className="col-12">
                                                        <label className="form-label fw-semibold">
                                                            <FiMapPin className="me-1" />
                                                            Address
                                                        </label>
                                                        <textarea
                                                            name="address"
                                                            value={userData.address}
                                                            onChange={handleInputChange}
                                                            className="form-control"
                                                            rows="3"
                                                            placeholder="Enter complete address"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Column - Settings & Image */}
                                    <div className="col-lg-4">
                                        <div className="row g-4">
                                            <div className="col-12">
                                                <div className="card border-0 shadow-sm">
                                                    <div className="card-header bg-light py-3">
                                                        <h6 className="mb-0">Account Settings</h6>
                                                    </div>
                                                    <div className="card-body">
                                                        <div className="mb-3">
                                                            <label className="form-label fw-semibold">Role *</label>
                                                            <select
                                                                name="role"
                                                                value={userData.role}
                                                                onChange={handleInputChange}
                                                                className={`form-select ${errors.role ? 'is-invalid' : ''}`}
                                                            >
                                                                <option value="">Select Role</option>
                                                                {roles.map((role) => (
                                                                    <option key={role.id} value={role.name}>
                                                                        {role.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {errors.role && (
                                                                <div className="invalid-feedback">{errors.role}</div>
                                                            )}
                                                        </div>

                                                        <div className="mb-3">
                                                            <label className="form-label fw-semibold">Status *</label>
                                                            <select
                                                                name="status"
                                                                value={userData.status}
                                                                onChange={handleInputChange}
                                                                className={`form-select ${errors.status ? 'is-invalid' : ''}`}
                                                            >
                                                                <option value="">Select Status</option>
                                                                <option value="active">Active</option>
                                                                <option value="inactive">Inactive</option>
                                                                <option value="banned">Banned</option>
                                                            </select>
                                                            {errors.status && (
                                                                <div className="invalid-feedback">{errors.status}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-12">
                                                <div className="card border-0 shadow-sm">
                                                    <div className="card-header bg-light py-3">
                                                        <h6 className="mb-0">Profile Image</h6>
                                                    </div>
                                                    <div className="card-body text-center">
                                                        <div className="mb-3">
                                                            {imagePreview ? (
                                                                <div className="position-relative d-inline-block">
                                                                    <img
                                                                        src={imagePreview}
                                                                        alt="Preview"
                                                                        className="rounded-circle border"
                                                                        style={{
                                                                            width: '120px',
                                                                            height: '120px',
                                                                            objectFit: 'cover'
                                                                        }}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={handleImageRemove}
                                                                        className="btn btn-danger btn-sm position-absolute"
                                                                        style={{ top: '-5px', right: '-5px' }}
                                                                    >
                                                                        <FiX />
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <div className="d-flex flex-column align-items-center">
                                                                    <div className="rounded-circle border d-flex align-items-center justify-content-center mb-3"
                                                                        style={{ width: '120px', height: '120px', backgroundColor: '#f8f9fa' }}>
                                                                        <FiUser size={48} className="text-muted" />
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="input-group">
                                                            <input
                                                                type="file"
                                                                name="image"
                                                                onChange={handleFileChange}
                                                                className="form-control"
                                                                accept="image/*"
                                                                id="profileImageInput"
                                                            />
                                                            <label className="btn btn-outline-primary" htmlFor="profileImageInput">
                                                                <FiUpload className="me-1" /> Upload
                                                            </label>
                                                        </div>
                                                        <small className="text-muted d-block mt-2">
                                                            Max file size: 5MB. Supported: JPG, PNG, GIF
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="row mt-4">
                                    <div className="col-12">
                                        <div className="d-flex justify-content-end gap-2">
                                            <button
                                                type="button"
                                                onClick={handleCancel}
                                                className="btn btn-outline-secondary px-4"
                                                disabled={loading}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn btn-primary px-4"
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        {isEditMode ? 'Updating...' : 'Creating...'}
                                                    </>
                                                ) : (
                                                    <>
                                                        <FiCheck className="me-2" />
                                                        {isEditMode ? 'Update User' : 'Add User'}
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            {/* Hidden file input for label trigger */}
            <input
                type="file"
                id="profileImageInput"
                name="image"
                onChange={handleFileChange}
                accept="image/*"
                style={{ display: 'none' }}
            />
        </div>
    );
};

export default AddNewuserLayer;